import { useState, useMemo } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import {
  AlertTriangle, Clock, Filter, Loader2, Search,
  ChevronDown, BarChart3, FileWarning, Copy, Check, Trash2, ExternalLink,
} from 'lucide-react'

interface ErrorStat {
  code: string
  count: number
}

interface ContextStat {
  context: string
  count: number
}

interface PageStat {
  page: string
  count: number
}

interface ErrorStatsResponse {
  total: number
  hours: number
  by_code: ErrorStat[]
  by_context: ContextStat[]
  top_pages: PageStat[]
}

interface ErrorLog {
  id: string
  profile_id: string | null
  error_code: string
  error_detail: string
  page_name: string | null
  fb_page_id: string | null
  group_name: string | null
  context: string | null
  created_at: number
}

interface ErrorLogsResponse {
  logs: ErrorLog[]
  hours: number
}

const HOURS_OPTIONS = [
  { value: 1, label: '1h' },
  { value: 6, label: '6h' },
  { value: 24, label: '24h' },
  { value: 72, label: '3d' },
  { value: 168, label: '7d' },
  { value: 720, label: '30d' },
]

const ERROR_CODE_COLORS: Record<string, string> = {
  SWITCH_FAILED: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
  IDENTITY_MISMATCH: 'bg-red-500/10 text-red-400 border-red-500/20',
  COMPOSER_NOT_FOUND: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  DIALOG_NOT_FOUND: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  PUBLISH_NOT_FOUND: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  NOT_MEMBER: 'bg-sky-500/10 text-sky-400 border-sky-500/20',
  NO_POST_PERMISSION: 'bg-sky-500/10 text-sky-400 border-sky-500/20',
  NO_IMAGES: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
  FB_GROUP_NOT_FOUND: 'bg-rose-500/10 text-rose-400 border-rose-500/20',
  FB_TEMP_BLOCKED: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
  UPLOAD_FAILED: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
  UNKNOWN: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20',
}

const CONTEXT_LABELS: Record<string, string> = {
  story: 'Stories',
  group_post: 'Posts em Grupos',
  invite: 'Convites',
  comment: 'Comentarios',
  unknown: 'Outros',
}

function getCodeColor(code: string) {
  return ERROR_CODE_COLORS[code] || 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20'
}

export function ErrorsTab() {
  const [hours, setHours] = useState(24)
  const [filterCode, setFilterCode] = useState<string | null>(null)
  const [searchPage, setSearchPage] = useState('')
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [deleting, setDeleting] = useState<string | null>(null)
  const queryClient = useQueryClient()

  const { data: statsData, isLoading: loadingStats } = useQuery<ErrorStatsResponse>({
    queryKey: ['automacao-fb', 'error-stats', hours],
    queryFn: () => api.get(`/automacao-fb/error-stats?hours=${hours}`),
    refetchInterval: 60000,
  })

  const { data: logsData, isLoading: loadingLogs } = useQuery<ErrorLogsResponse>({
    queryKey: ['automacao-fb', 'error-logs', hours, filterCode, searchPage],
    queryFn: () => {
      const params = new URLSearchParams({ hours: String(hours) })
      if (filterCode) params.set('error_code', filterCode)
      if (searchPage.trim()) params.set('page_name', searchPage.trim())
      return api.get(`/automacao-fb/error-logs?${params}`)
    },
    refetchInterval: 60000,
  })

  const stats = statsData
  const logs = logsData?.logs ?? []

  const codePercentages = useMemo(() => {
    if (!stats || stats.total === 0) return []
    return stats.by_code.map(c => ({
      ...c,
      pct: ((c.count / stats.total) * 100).toFixed(1),
    }))
  }, [stats])

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-red-500/10 border border-red-500/20">
            <AlertTriangle className="h-4.5 w-4.5 text-red-400" />
          </div>
          <div>
            <h2 className="text-base font-bold text-foreground">Erros do Agent</h2>
            <p className="text-[10px] text-muted-foreground">
              {stats ? `${stats.total} erros nas ultimas ${hours}h` : 'Carregando...'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Clear logs button */}
          {stats && stats.total > 0 && (
            <button
              onClick={async () => {
                if (!confirm(`Limpar todos os ${stats.total} logs de erro?`)) return
                try {
                  await api.delete('/automacao-fb/error-logs')
                  queryClient.invalidateQueries({ queryKey: ['automacao-fb'] })
                } catch (err: any) {
                  alert(`Erro: ${err.message}`)
                }
              }}
              className="flex items-center gap-1 rounded-lg border border-red-500/30 bg-red-500/10 px-2.5 py-1.5 text-[11px] font-medium text-red-400 hover:bg-red-500/20 transition-colors"
            >
              <Trash2 className="h-3 w-3" />
              Limpar
            </button>
          )}

          {/* Time range selector */}
          <div className="flex items-center gap-1 rounded-lg border border-border bg-background p-0.5">
            {HOURS_OPTIONS.map(opt => (
              <button
                key={opt.value}
                onClick={() => setHours(opt.value)}
                className={`px-2.5 py-1.5 text-[11px] font-medium rounded-md transition-colors ${
                  hours === opt.value
                    ? 'bg-indigo-500/15 text-indigo-400'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loadingStats ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-indigo-400" />
        </div>
      ) : stats && stats.total > 0 ? (
        <>
          {/* Stats cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* By error code */}
            <div className="col-span-2 rounded-xl border border-border bg-card p-4">
              <div className="flex items-center gap-2 mb-3">
                <BarChart3 className="h-4 w-4 text-muted-foreground" />
                <h3 className="text-xs font-semibold text-foreground">Por codigo de erro</h3>
              </div>
              <div className="space-y-2">
                {codePercentages.map(c => (
                  <button
                    key={c.code}
                    onClick={() => setFilterCode(filterCode === c.code ? null : c.code)}
                    className={`w-full flex items-center gap-3 rounded-lg px-3 py-2 transition-colors text-left ${
                      filterCode === c.code ? 'ring-1 ring-indigo-500/50' : ''
                    } hover:bg-muted/50`}
                  >
                    <span className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[10px] font-mono font-medium ${getCodeColor(c.code)}`}>
                      {c.code}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                        <div
                          className="h-full rounded-full bg-indigo-500/60"
                          style={{ width: `${c.pct}%` }}
                        />
                      </div>
                    </div>
                    <span className="text-xs font-medium text-foreground tabular-nums w-10 text-right">{c.count}</span>
                    <span className="text-[10px] text-muted-foreground tabular-nums w-12 text-right">{c.pct}%</span>
                  </button>
                ))}
              </div>
            </div>

            {/* By context */}
            <div className="rounded-xl border border-border bg-card p-4">
              <div className="flex items-center gap-2 mb-3">
                <Filter className="h-4 w-4 text-muted-foreground" />
                <h3 className="text-xs font-semibold text-foreground">Por contexto</h3>
              </div>
              <div className="space-y-2">
                {stats.by_context.map(c => (
                  <div key={c.context} className="flex items-center justify-between px-2 py-1.5">
                    <span className="text-xs text-foreground/85">{CONTEXT_LABELS[c.context] || c.context}</span>
                    <span className="text-xs font-medium text-foreground tabular-nums">{c.count}</span>
                  </div>
                ))}
              </div>

              {stats.top_pages.length > 0 && (
                <>
                  <div className="border-t border-border mt-3 pt-3">
                    <div className="flex items-center gap-2 mb-2">
                      <FileWarning className="h-3.5 w-3.5 text-muted-foreground" />
                      <h4 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Top paginas com erro</h4>
                    </div>
                    <div className="space-y-1">
                      {stats.top_pages.slice(0, 10).map(p => (
                        <button
                          key={p.page}
                          onClick={() => setSearchPage(p.page)}
                          className="flex w-full items-center justify-between px-2 py-1 rounded-md hover:bg-muted/50 transition-colors"
                        >
                          <span className="text-[11px] text-foreground/80 truncate max-w-[150px]">{p.page}</span>
                          <span className="text-[11px] font-medium text-foreground tabular-nums">{p.count}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Logs table */}
          <div className="rounded-xl border border-border bg-card">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <div className="flex items-center gap-2">
                <h3 className="text-xs font-semibold text-foreground">Erros recentes</h3>
                {logs.length > 0 && (
                  <button
                    onClick={() => {
                      const text = logs.map(l => {
                        const date = new Date(l.created_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
                        const page = l.page_name || l.group_name || '-'
                        return `[${date}] ${l.error_code} | ${page}${l.context ? ` (${l.context})` : ''} | ${l.error_detail}`
                      }).join('\n')
                      navigator.clipboard.writeText(text)
                      setCopied(true)
                      setTimeout(() => setCopied(false), 2000)
                    }}
                    className="flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[10px] text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                  >
                    {copied ? <Check className="h-3 w-3 text-emerald-400" /> : <Copy className="h-3 w-3" />}
                    {copied ? 'Copiado!' : 'Copiar todos'}
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                {filterCode && (
                  <button
                    onClick={() => setFilterCode(null)}
                    className="flex items-center gap-1 rounded-md border border-indigo-500/30 bg-indigo-500/10 px-2 py-1 text-[10px] text-indigo-400 hover:bg-indigo-500/20 transition-colors"
                  >
                    {filterCode}
                    <span className="ml-1">&times;</span>
                  </button>
                )}
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                  <input
                    type="text"
                    value={searchPage}
                    onChange={(e) => setSearchPage(e.target.value)}
                    placeholder="Filtrar pagina..."
                    className="rounded-md border border-border bg-background pl-7 pr-3 py-1.5 text-[11px] text-foreground placeholder:text-muted-foreground focus:border-primary/50 focus:outline-none w-40"
                  />
                </div>
              </div>
            </div>

            {loadingLogs ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-indigo-400" />
              </div>
            ) : logs.length === 0 ? (
              <div className="py-8 text-center">
                <p className="text-xs text-muted-foreground">Nenhum erro encontrado com esses filtros</p>
              </div>
            ) : (
              <div className="divide-y divide-border max-h-[500px] overflow-y-auto">
                {logs.map(log => {
                  const isExpanded = expandedLogId === log.id
                  return (
                    <button
                      key={log.id}
                      onClick={() => setExpandedLogId(isExpanded ? null : log.id)}
                      className="w-full text-left px-4 py-2.5 hover:bg-muted/30 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <span className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[10px] font-mono font-medium shrink-0 ${getCodeColor(log.error_code)}`}>
                          {log.error_code}
                        </span>
                        {log.page_name ? (
                          log.fb_page_id ? (
                            <a
                              href={`https://facebook.com/${log.fb_page_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="text-[11px] text-indigo-400 hover:text-indigo-300 hover:underline truncate flex-1"
                              title={`Abrir ${log.page_name} no Facebook`}
                            >
                              {log.page_name}
                            </a>
                          ) : (
                            <span className="text-[11px] text-foreground/80 truncate flex-1">{log.page_name}</span>
                          )
                        ) : (
                          <span className="text-[11px] text-foreground/80 truncate flex-1">
                            {log.group_name || 'Sem pagina'}
                          </span>
                        )}
                        {log.context && (
                          <span className="text-[10px] text-muted-foreground rounded-full bg-muted px-2 py-0.5 shrink-0">
                            {CONTEXT_LABELS[log.context] || log.context}
                          </span>
                        )}
                        <span className="text-[10px] text-muted-foreground tabular-nums shrink-0">
                          {new Date(log.created_at).toLocaleString('pt-BR', {
                            day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
                          })}
                        </span>
                        <ChevronDown className={`h-3 w-3 text-muted-foreground shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                      </div>
                      {isExpanded && (
                        <div className="mt-2 rounded-lg bg-muted/50 border border-border p-3">
                          <p className="text-[11px] text-foreground/90 whitespace-pre-wrap break-all font-mono leading-relaxed">
                            {log.error_detail}
                          </p>
                          <div className="mt-2 flex flex-wrap gap-2 text-[10px] text-muted-foreground">
                            {log.page_name && (
                              <span>Pagina: {log.fb_page_id ? (
                                <a
                                  href={`https://facebook.com/${log.fb_page_id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="text-indigo-400 hover:underline"
                                >
                                  {log.page_name}
                                </a>
                              ) : log.page_name}</span>
                            )}
                            {log.group_name && <span>Grupo: {log.group_name}</span>}
                            {log.profile_id && <span>Perfil: {log.profile_id.substring(0, 8)}...</span>}
                          </div>
                          {(log.error_code === 'SWITCH_FAILED' || log.error_code === 'NOT_MEMBER') && log.page_name && (
                            <div className="mt-2 flex items-center gap-2 pt-2 border-t border-border">
                              {log.fb_page_id && (
                                <a
                                  href={`https://facebook.com/${log.fb_page_id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[10px] text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                                >
                                  <ExternalLink className="h-3 w-3" />
                                  Verificar no FB
                                </a>
                              )}
                              <button
                                onClick={async (e) => {
                                  e.stopPropagation()
                                  if (!confirm(`Deletar a pagina "${log.page_name}" de todas as conexoes deste perfil?`)) return
                                  setDeleting(log.id)
                                  try {
                                    const conns: any = await api.get(`/automacao-fb/connections`)
                                    const matches = (conns.connections || conns || []).filter(
                                      (c: any) => c.fb_page_name === log.page_name && (!log.profile_id || c.profile_id === log.profile_id)
                                    )
                                    for (const c of matches) {
                                      await api.delete(`/automacao-fb/connections/${c.id}`)
                                    }
                                    alert(matches.length > 0 ? `${matches.length} conexao(oes) deletada(s)` : 'Nenhuma conexao encontrada com esse nome')
                                    queryClient.invalidateQueries({ queryKey: ['automacao-fb'] })
                                  } catch (err: any) {
                                    alert(`Erro: ${err.message}`)
                                  } finally {
                                    setDeleting(null)
                                  }
                                }}
                                disabled={deleting === log.id}
                                className="flex items-center gap-1 rounded-md border border-red-500/30 bg-red-500/10 px-2 py-1 text-[10px] text-red-400 hover:bg-red-500/20 transition-colors disabled:opacity-50"
                              >
                                {deleting === log.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                                Deletar pagina
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-500/10 border border-emerald-500/20 mb-4">
            <AlertTriangle className="h-6 w-6 text-emerald-400" />
          </div>
          <h3 className="text-sm font-semibold text-foreground mb-1">Nenhum erro registrado</h3>
          <p className="text-xs text-muted-foreground max-w-sm">
            Nas ultimas {hours}h nenhum erro foi reportado pelo agent. Bom sinal!
          </p>
        </div>
      )}
    </div>
  )
}
